import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getUserRole, isStudentUser } from '../utils/userRole';

const Header = ({ siteText, minimal = false }) => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [menuOpen, setMenuOpen] = useState(false);

    const getText = (key, defaultVal) => {
        const item = siteText?.find(t => t.key === key);
        return item ? item.content : defaultVal;
    };

    const isVisible = (key) => {
        const item = siteText?.find(t => t.key === key);
        return item ? item.content === 'true' : true;
    };

    const handleLogout = async () => {
        setMenuOpen(false);
        await logout();
        navigate('/');
    };

    const closeMenu = () => setMenuOpen(false);

    useEffect(() => {
        setMenuOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (!menuOpen) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') setMenuOpen(false);
        };
        document.addEventListener('keydown', onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [menuOpen]);

    const showHome = isVisible('show_home');
    const showCourses = isVisible('show_courses');
    const showPrices = isVisible('show_prices');
    const role = user ? getUserRole(user) : null;

    const navItems = [];
    if (!minimal && showHome) {
        navItems.push({ to: '/', label: getText('site_nav_home', 'Home') });
    }
    if (showCourses) {
        navItems.push({ to: '/courses', label: getText('site_nav_courses', 'Courses') });
    }
    if (showPrices) {
        navItems.push({ to: '/prices', label: getText('site_nav_prices', 'Prices') });
    }
    if (!minimal) {
        navItems.push({ to: '/portal', label: 'Портал' });
    }
    if (!minimal && user) {
        if (role === 'teacher') {
            navItems.push({ to: '/teacher', label: getText('site_nav_dashboard', 'Dashboard') });
        } else if (isStudentUser(user)) {
            navItems.push({ to: '/student', label: getText('site_nav_mystudy', 'My Study') });
        }
        // Portal role: Портал already listed above
    }

    return (
        <header className={`header animate-fade-in${menuOpen ? ' is-menu-open' : ''}`}>
            <div className="container site-header-bar">
                <div className="logo font-header">
                    <Link to="/" className="logo-link" onClick={closeMenu}>
                        <span className="logo-icon">{getText('site_header_logo_icon', '🌿')}</span>
                        <span className="logo-text">{getText('site_header_logo_text', 'АлексКласс')}</span>
                    </Link>
                </div>

                <nav className="nav nav-desktop" aria-label="Main">
                    <ul className="nav-desktop-list font-header">
                        {navItems.map((item) => (
                            <li key={item.to}>
                                <Link to={item.to} className="nav-link">{item.label}</Link>
                            </li>
                        ))}
                        {!minimal && (
                            !user ? (
                                <li>
                                    <Link to="/login" className="btn btn-primary shadow-glow">
                                        {getText('site_nav_login', 'Log In')}
                                    </Link>
                                </li>
                            ) : (
                                <li>
                                    <button type="button" onClick={handleLogout} className="btn-logout">
                                        {getText('site_nav_logout', 'Logout')}
                                    </button>
                                </li>
                            )
                        )}
                    </ul>
                </nav>

                {!minimal && (
                    <button
                        type="button"
                        className={`nav-burger${menuOpen ? ' is-open' : ''}`}
                        aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                        aria-expanded={menuOpen}
                        aria-controls="site-mobile-menu"
                        onClick={() => setMenuOpen((v) => !v)}
                    >
                        <span className="nav-burger-box" aria-hidden="true">
                            <span className="nav-burger-line" />
                            <span className="nav-burger-line" />
                            <span className="nav-burger-line" />
                        </span>
                    </button>
                )}
            </div>

            {!minimal && (
                <>
                    <div
                        className={`nav-backdrop${menuOpen ? ' is-open' : ''}`}
                        onClick={closeMenu}
                        aria-hidden={!menuOpen}
                    />
                    <nav
                        id="site-mobile-menu"
                        className={`nav-drawer${menuOpen ? ' is-open' : ''}`}
                        aria-hidden={!menuOpen}
                        aria-label="Mobile"
                    >
                        <ul className="nav-drawer-list font-header">
                            {navItems.map((item) => (
                                <li key={item.to}>
                                    <Link to={item.to} className="nav-drawer-link" onClick={closeMenu}>
                                        {item.label}
                                    </Link>
                                </li>
                            ))}
                            {!user ? (
                                <li>
                                    <Link to="/login" className="nav-drawer-cta" onClick={closeMenu}>
                                        {getText('site_nav_login', 'Log In')}
                                    </Link>
                                </li>
                            ) : (
                                <li>
                                    <button type="button" className="nav-drawer-logout" onClick={handleLogout}>
                                        {getText('site_nav_logout', 'Logout')}
                                    </button>
                                </li>
                            )}
                        </ul>
                    </nav>
                </>
            )}

            <style dangerouslySetInnerHTML={{ __html: `
        .header {
          height: var(--header-height);
          background: rgba(255, 255, 255, 0.92);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border-bottom: 1px solid rgba(226, 232, 240, 0.6);
          position: sticky;
          top: 0;
          z-index: 1000;
          overflow: visible;
          transition: var(--transition-smooth);
        }
        .site-header-bar {
          position: relative;
          z-index: 1002;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          min-width: 0;
        }
        .logo {
          min-width: 0;
          flex: 1 1 auto;
        }
        .logo-link {
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 0;
          text-decoration: none;
          color: inherit;
        }
        .logo-text {
          font-size: 22px;
          font-weight: 800;
          color: var(--primary);
          letter-spacing: -0.02em;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .logo-icon { font-size: 24px; flex-shrink: 0; }

        .nav-desktop { flex-shrink: 0; }
        .nav-desktop-list {
          display: flex;
          gap: 1.25rem;
          align-items: center;
          font-size: 0.875rem;
          letter-spacing: 0.02em;
          list-style: none;
          margin: 0;
          padding: 0;
        }

        .nav-link {
            color: var(--text-secondary);
            font-weight: 500;
            transition: var(--transition-fast);
            position: relative;
            margin-right: 0;
            white-space: nowrap;
        }
        .nav-link:hover { color: var(--primary); }
        .nav-link::after {
            content: ''; position: absolute; bottom: -4px; left: 0;
            width: 0; height: 2px; background: var(--accent);
            transition: var(--transition-fast);
        }
        .nav-link:hover::after { width: 100%; }

        .btn-primary {
            background: var(--primary); color: white; border-radius: 12px;
            padding: 10px 24px; font-weight: 700;
        }
        .shadow-glow { box-shadow: 0 4px 14px 0 rgba(67, 97, 238, 0.3); }
        .btn-logout {
            background: none; border: none; font-size: 13px; font-weight: 600;
            color: var(--text-muted); cursor: pointer; transition: color 0.2s;
        }
        .btn-logout:hover { color: var(--danger, #ef4444); }

        .nav-burger {
            display: none;
            flex-shrink: 0;
            width: 44px;
            height: 44px;
            margin: 0;
            border: 1.5px solid #e2e8f0;
            background: #fff;
            border-radius: 12px;
            padding: 0;
            cursor: pointer;
            align-items: center;
            justify-content: center;
            -webkit-tap-highlight-color: transparent;
            box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
            z-index: 1003;
            position: relative;
        }
        .nav-burger:hover,
        .nav-burger.is-open {
            background: #f8fafc;
            border-color: #cbd5e1;
        }
        .nav-burger-box {
            display: flex;
            flex-direction: column;
            justify-content: center;
            gap: 5px;
            width: 20px;
        }
        .nav-burger-line {
            display: block;
            width: 100%;
            height: 2px;
            border-radius: 2px;
            background: #0f172a;
            transition: transform 0.2s ease, opacity 0.2s ease;
        }
        .nav-burger.is-open .nav-burger-line:nth-child(1) {
            transform: translateY(7px) rotate(45deg);
        }
        .nav-burger.is-open .nav-burger-line:nth-child(2) {
            opacity: 0;
        }
        .nav-burger.is-open .nav-burger-line:nth-child(3) {
            transform: translateY(-7px) rotate(-45deg);
        }

        .nav-backdrop {
            display: none;
            position: fixed;
            inset: 0;
            top: var(--header-height);
            background: rgba(15, 23, 42, 0.35);
            opacity: 0;
            pointer-events: none;
            transition: opacity 0.2s ease;
            z-index: 1000;
        }
        .nav-backdrop.is-open {
            opacity: 1;
            pointer-events: auto;
        }

        .nav-drawer {
            display: none;
            position: fixed;
            top: var(--header-height);
            left: 0;
            right: 0;
            z-index: 1001;
            background: rgba(255, 255, 255, 0.98);
            border-bottom: 1px solid rgba(226, 232, 240, 0.9);
            box-shadow: 0 16px 40px rgba(15, 23, 42, 0.12);
            max-height: calc(100dvh - var(--header-height));
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
            transform: translateY(-8px);
            opacity: 0;
            pointer-events: none;
            transition: opacity 0.2s ease, transform 0.2s ease;
            padding: 8px 0 20px;
        }
        .nav-drawer.is-open {
            opacity: 1;
            transform: translateY(0);
            pointer-events: auto;
        }
        .nav-drawer-list {
            list-style: none;
            margin: 0;
            padding: 8px 20px 0;
            display: flex;
            flex-direction: column;
            gap: 4px;
        }
        .nav-drawer-link {
            display: block;
            padding: 14px 12px;
            border-radius: 12px;
            color: #0f172a;
            font-weight: 700;
            font-size: 16px;
            text-decoration: none;
        }
        .nav-drawer-link:hover,
        .nav-drawer-link:active {
            background: #f1f5f9;
            color: var(--primary);
        }
        .nav-drawer-cta {
            display: block;
            margin-top: 8px;
            text-align: center;
            background: var(--primary);
            color: white !important;
            border-radius: 12px;
            padding: 14px 16px;
            font-weight: 800;
            font-size: 15px;
            text-decoration: none;
            box-shadow: 0 4px 14px 0 rgba(67, 97, 238, 0.3);
        }
        .nav-drawer-logout {
            width: 100%;
            margin-top: 4px;
            border: none;
            background: transparent;
            text-align: left;
            padding: 14px 12px;
            border-radius: 12px;
            color: #64748b;
            font-weight: 700;
            font-size: 15px;
            cursor: pointer;
        }
        .nav-drawer-logout:hover {
            background: #fef2f2;
            color: #ef4444;
        }

        @media (max-width: 860px) {
          .nav-desktop { display: none !important; }
          .nav-burger { display: inline-flex !important; }
          .nav-backdrop,
          .nav-drawer { display: block; }
          .logo-text { font-size: 18px; }
        }
      ` }} />
        </header>
    );
};

export default Header;
